import { Budget } from '@/types/financial';
import { v4 as uuidv4 } from 'uuid';
import { normalizeAmount } from '@/utils/formValidation';
import {
  saveBudgetToSupabase,
  deleteBudgetFromSupabase,
} from '@/lib/supabase-operations';
import { logger } from '@/utils/logger';

export const createBudget = async (
  budgetData: Omit<Budget, 'id' | 'createdAt'>
): Promise<Budget> => {
  logger.info('Creating budget', { name: budgetData.name });

  // Normalize numeric values
  const normalizedBudget = {
    ...budgetData,
    amount: normalizeAmount(budgetData.amount),
    spent: normalizeAmount(budgetData.spent || 0)
  };

  // Validate required fields
  if (!normalizedBudget.name?.trim()) {
    throw new Error('Nome do orçamento é obrigatório');
  }
  if (!normalizedBudget.categoryId?.trim()) {
    throw new Error('Categoria é obrigatória');
  }
  if (normalizedBudget.amount <= 0) {
    throw new Error('Valor do orçamento deve ser maior que zero');
  }
  if (!normalizedBudget.period?.trim()) {
    throw new Error('Período é obrigatório');
  }

  const newBudget: Budget = {
    ...normalizedBudget,
    id: uuidv4(),
    createdAt: new Date().toISOString(),
  };

  await saveBudgetToSupabase(newBudget);
  logger.info('Budget created successfully', { id: newBudget.id });
  
  return newBudget;
};

export const updateBudget = async (
  id: string,
  updates: Partial<Budget>,
  currentBudget: Budget
): Promise<Budget> => {
  logger.info('Updating budget', { id });

  // Normalize numeric values in updates
  const normalizedUpdates = { ...updates };

  if (updates.amount !== undefined) {
    normalizedUpdates.amount = normalizeAmount(updates.amount);
  }
  if (updates.spent !== undefined) {
    normalizedUpdates.spent = normalizeAmount(updates.spent);
  }

  const updatedBudget = { ...currentBudget, ...normalizedUpdates };
  
  await saveBudgetToSupabase(updatedBudget);
  logger.info('Budget updated successfully', { id });
  
  return updatedBudget;
};

export const deleteBudget = async (id: string): Promise<void> => {
  logger.info('Deleting budget', { id });
  await deleteBudgetFromSupabase(id);
  logger.info('Budget deleted successfully', { id });
};
